import isObject from "lodash/fp/isObject";
import each from "lodash/fp/each";
import DialogTemplate from "./dialog/dialog.html";
import DialogController from "./dialog/dialog.controller";
import {
  toMilliseconds,
  toPhoneObject,
  multiplyFloatingPointByCurrency,
} from "../../core/utils";
import constants from "../../core/constants";
import set from "lodash/fp/set";
import find from "lodash/fp/find";
import isNil from "lodash/fp/isNil";
import findIndex from "lodash/fp/findIndex";
import zip from "lodash/fp/zip";
import cloneDeep from "lodash/fp/cloneDeep";
import isFunction from "lodash/fp/isFunction";

class Controller {
  constructor(Events, $mdDialog, $scope, ResetFields, Lists) {
    "ngInject";

    this.Events = Events;
    this.$mdDialog = $mdDialog;
    this.$scope = $scope;
    this.ResetFields = ResetFields;
    this.Lists = Lists.all;
    this.formatConditions = this.formatConditions.bind(this);
    this.bindConditions = this.bindConditions.bind(this);
  }

  $onInit() {
    // IfConditions
    this.options.conditionalFields = {};
    this.options.conditionalBindings = [];
    this.initFields = cloneDeep(this.options.fields);
    this.options.fields.forEach(this.formatConditions);
    this.model = this.model || {};

    const formatFiles = (filesField) => {
      if (filesField.files.length > 0) {
        if (filesField.allowMultiple) {
          return filesField.files.map((file) => file.name.source);
        }
        return filesField.files[0].source;
      }
      return filesField.files;
    };

    const formatLocation = (locationField) => {
      const locationData = locationField.value;
      return {
        name: locationData.name,
        coords: {
          lat: locationData.location.value[1],
          lng: locationData.location.value[0],
        },
      };
    };

    const handleObjectFieldValue = (field) => {
      if (field.value._id) {
        this.model[field.name] = field.value._id;
      } else if (field.date && field.formatter) {
        this.model[field.name] = field.formatter(field.value);
      } else if (field.date && !field.formatter) {
        this.model[field.name] = field.value.toISOString();
      } else if (field.dateTime) {
        this.model[field.name] = field.value.toISOString();
      } else if (field.time) {
        this.model[field.name] = field.formatter(field.value);
      } else if (field.isLocation) {
        this.model[field.name] = {
          type: "Point",
          coordinates: field.value.value,
        };
      } else if (field.isCheckBox) {
        this.model[field.name] = field.value.value;
      } else if (field.isPromotionValue) {
        Object.assign(this.model, field.value);
      } else if (field.isTextarea) {
        this.model[field.name] = field.value.value;
      } else if (field.isCityLanguages) {
        this.model.languages = field.value;
      } else {
        // Including isScheduleDays
        this.model[field.name] = field.value;
      }
    };

    // eslint-disable-next-line complexity
    const handleNonObjectFieldValue = (field) => {
      if (field.sortable || field.editable) {
        this.model[field.name] = field.formatter
          ? field.formatter(field.value)
          : field.value;
      } else if (field.isItemsList) {
        this.model[field.name] = field.formatter
          ? field.formatter(field.items)
          : field.value;
      } else if (field.isLocationsList) {
        this.model[field.name] = field.items.map(formatLocation);
      } else if (field.isColorPicker && field.eventsColors) {
        this.model[field.name] = {
          r: field.r,
          g: field.g,
          b: field.b,
          a: field.a,
        };
      } else if (field.isPricingParameters) {
        this.model[field.name] = {
          base_fare: multiplyFloatingPointByCurrency(field.base_fare),
          base_distance: field.base_distance,
          distance_bucket_fare: multiplyFloatingPointByCurrency(
            field.distance_bucket_fare
          ),
          distance_bucket_size: field.distance_bucket_size,
          base_time: field.base_time,
          time_bucket_size: field.time_bucket_size,
          time_bucket_fare: multiplyFloatingPointByCurrency(
            field.time_bucket_fare
          ),
          maximum_fare: field.maximum_fare,
          rounding_off_limit: field.rounding_off_limit,
        };
      } else if (field.isPhoneObject) {
        const phoneData = field.name || "phone_data";
        const city = this.options.fields.find((field) => field.name === "city");
        if (!isNil(city)) {
          this.model[phoneData] = toPhoneObject(field.value, city.value);
        }
      } else if (field.isResourceList && !field.isSelectable) {
        this.model[field.name] = field.formatter(field.items);
      } else if (field.isResourceList && field.isSelectable) {
        const _ids = field.formatter(field.items);
        const _selected = field.items.map((item) => item.selectedItemInList);
        this.model[field.name] = zip(_ids, _selected).map((selected) => {
          const selectedField = field.selectTitle.toLowerCase();
          return {
            id: selected[0],
            [selectedField]: selected[1],
          };
        });
      } else if (field.isI18nText) {
        if (Array.isArray(this.model.languages)) {
          this.model[field.name] = this.model.languages.reduce((i18n, lang) => {
            i18n[lang] = field.i18n[lang];

            return i18n;
          }, {});
        } else {
          this.model[field.name] = field.i18n;
        }
      } else if (field.isTimezone) {
        this.model[field.name] = toMilliseconds(field.value);
      } else if (field.isPayment) {
        this.model.payment = {
          ...this.model.payment,
          [field.name]: field.value,
        };
      } else if (field.isObjectsList) {
        this.model.ride_late_deduction = field.items;
      } else if (field.isDocumentUpload) {
        this.model[field.name] = formatFiles(field);
      } else if (field.isCheckBox) {
        this.model[field.name] = Boolean(field.value);
      } else if (field.isRadio) {
        this.model[field.name] = field.value === "true";
      } else if (field.isCompoundSwitch) {
        field.children.forEach((child) => {
          this.model[child.name] = child.value;
        });
      } else {
        this.model[field.name] = field.formatter
          ? field.formatter(field.value)
          : field.value;
      }
    };

    const mapNameValue = (field) => {
      if (field.renderIf) {
        if (isObject(field.value)) {
          handleObjectFieldValue(field);
        } else {
          handleNonObjectFieldValue(field);
        }
      } else {
        // Delete from the model if conditional rendering is false;
        delete this.model[field.name];
      }
    };

    this.openDialog = () => {
      const fieldsLength = Object.keys(this.options.conditionalFields).length;
      if (fieldsLength && fieldsLength > 0) {
        this.cancelIfConditionWatch = this.$scope.$watch(
          "$ctrl.options.conditionalFields",
          this.bindConditions,
          true
        );
      }
      const add = this.$mdDialog.show({
        controller: DialogController,
        controllerAs: "$ctrl",
        template: DialogTemplate,
        autoWrap: false,
        bindToController: true,
        clickOutsideToClose: true,
        locals: {
          options: this.options,
          selectedDays: this.selectedDays,
        },
      });

      add.then(
        (obj) => {
          // Map ride late deduction, if exists, on submit, a quick fix
          // TODO: refactor this to be in deduction plans controller it self
          const justifiedFields = cloneDeep(obj.fields);
          const rideLateDeduction = find(
            (field) => field.name === "ride_late_deduction"
          )(justifiedFields);
          const rideLateDeductionIndex = findIndex(
            (field) => field.name === "ride_late_deduction"
          )(justifiedFields);
          if (!isNil(rideLateDeduction) && !isNil(rideLateDeduction.items)) {
            const items = rideLateDeduction.items;
            const justifiedItems = items.map((item) => {
              if (item && item.type && item.threshold) {
                const realAmount =
                  item.type.name === "percentage"
                    ? item.amount / 100
                    : item.amount * constants.currencyBy;
                const realThreshold = item.threshold * constants.fromMinToMilli;
                return {
                  type: item.type.name,
                  amount: realAmount,
                  threshold: realThreshold,
                };
              }
              return {
                type: "",
                amount: 0,
                threshold: 0,
              };
            });
            const justifiedRideLateDeduction = set("items")(justifiedItems)(
              rideLateDeduction
            );
            if (!isNil(rideLateDeductionIndex)) {
              justifiedFields[rideLateDeductionIndex].items =
                justifiedRideLateDeduction.items;
            }
          }

          const weekDays = find((field) => field.name === "weekdays")(
            justifiedFields
          );
          const weekDaysIndex = findIndex((field) => field.name === "weekdays")(
            justifiedFields
          );
          if (!isNil(weekDays) && !isNil(weekDays.value)) {
            const days = weekDays.value;
            this.selectedDays = days;
            const daysNumbers = [];
            days.forEach((day) => {
              if (day.isSelected) {
                daysNumbers.push(day.value);
              }
            });
            const justifiedWeekDays = set("value")(daysNumbers)(weekDays);
            if (!isNil(weekDaysIndex)) {
              justifiedFields[weekDaysIndex].value = justifiedWeekDays.value;
            }
          }
          each(mapNameValue)(obj.fields);
          this.model = { ...this.model, ...this.addItemHiddenFields };
          each(mapNameValue)(justifiedFields);
          if (isFunction(this.addItemCustomizeOptions)) {
            this.addItemCustomizeOptions(this.model);
          }
          this.Events.emit(this.eventName, this.model);
        },
        () => {
          // Popup close
          !isNil(this.cancelIfConditionWatch) && this.cancelIfConditionWatch();
          this.ResetFields.reset(this.options.fields);
          const languagesfield = this.options.fields.find(
            (field) => field.name === "name"
          );
          languagesfield.languages = this.Lists.requiredCityLanguages;
        }
      );
    };
  }

  $onDestroy() {
    !isNil(this.cancelIfConditionWatch) && this.cancelIfConditionWatch();
    this.options.fields = this.initFields;
    this.$mdDialog.cancel();
  }

  formatConditions(field) {
    if (typeof field.renderIf === "string") {
      const convertedExpression = field.renderIf.replace(
        /\{\{(\S+)\}\}/g,
        (_match, p1) => {
          const conditionField = this.options.fields.find(
            (field) => field.name === p1
          );
          if (conditionField) {
            this.options.conditionalFields[p1] = conditionField;
          }
          return `$ctrl.options.conditionalFields.${p1}`;
        }
      );
      this.options.conditionalBindings.push({
        field,
        expression: convertedExpression,
      });
    } else {
      field.renderIf = true;
    }
  }

  bindConditions() {
    this.options.conditionalBindings.forEach((condition) => {
      condition.field.renderIf = this.$scope.$eval(condition.expression);
    });
  }
}

export default Controller;



// WEBPACK FOOTER //
// ./src/components/addItem/addItem.controller.js